import { Link } from "@tanstack/react-router";

import { ErrorSurface } from "@/components/error-surfaces/error-surface";
import { Button } from "@/components/ui/button";

/**
 * Where "Request access" goes. The old stack never wired the action on its 403
 * variant (only its error gallery passed a no-op), but its account panel's own
 * "Request Access" button pointed here — the ServiceNow catalog item that
 * grants Spec Tool permission levels. Carried over rather than invented, and
 * wired this time.
 */
export const REQUEST_ACCESS_URL =
  "https://lennar.service-now.com/ssp?id=sc_cat_item_guide&sys_id=7dc4538bdbb788181c445ad3ca9619f7";

/**
 * The 403 surface: a permission wall, not a failure. Rendered for a
 * `role-forbidden`-tagged error (components/router-fallbacks.tsx).
 *
 * A denial has to be actionable, so it carries two ways out: home, and the
 * request that would lift the wall.
 */
export function Forbidden({ message }: { message?: string }) {
  return (
    <ErrorSurface
      code="403"
      title="You don't have access to this"
      message="Your account doesn't have permission to view this page. If you think this is a mistake, reach out to your administrator."
      // Python wrote this FOR the user (the `role-forbidden` tag is on the
      // scrub allowlist), and it is the only place the specific capability is
      // named — so it sits alongside the standard copy rather than replacing it.
      detail={message ? <p className="text-lennar-grey-500 pt-1 text-sm">{message}</p> : undefined}
      actions={
        <>
          <Button asChild className="w-full">
            {/* Home is `/`, the landing dispatcher — so this follows the
                audience's landing path rather than naming a destination. */}
            <Link to="/">Go home</Link>
          </Button>
          {/* A new tab: the denial screen stays put so the user can retry once
              the request is filed, instead of losing where they were walled out. */}
          <Button asChild variant="outline" className="w-full">
            <a href={REQUEST_ACCESS_URL} target="_blank" rel="noreferrer noopener">
              Request access
            </a>
          </Button>
        </>
      }
    />
  );
}
